<template>
  <div class="ts-site">
    <component :is="shell.header" v-if="showChrome" />
    <main id="main-content" class="ts-app ts-main" :class="pageClass">
      <pages-navigation v-if="showChrome" />
      <!-- Tabs of the section (Stellensuche / Stellenkatalog …), full width above the page -->
      <ts-section-tabs v-if="showChrome" />
      <info-banner />

      <!--
        One router-view in the same place for both kinds of pages: switching between a redesigned
        and a legacy page of one section (search → results) must not re-create the section.
        Redesigned pages lay out their own full-width sections; legacy pages get the old grid
        with the right sidebar.
      -->
      <div :id="isTalentspacePage ? null : 'content'" :class="{ 'container pt-0 pb-5': !isTalentspacePage }">
        <div :class="{ row: !isTalentspacePage }">
          <div :class="{ 'col-12': !isTalentspacePage, 'col-xl-10': !isTalentspacePage && showChrome }">
            <router-view />
          </div>
          <div v-if="!isTalentspacePage && showChrome" class="col-12 col-xl-2 py-2 py-md-4">
            <right-sidebar :class="rightSidebarClass" />
          </div>
        </div>
        <information-edunetin v-if="!isTalentspacePage && sid === 'edunetin'" />
      </div>
    </main>
    <template v-if="showChrome">
      <component :is="shell.footer" />
      <terms-modal />
    </template>
    <jobs-filter :is-welcome="isWelcome" />
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';
import PagesNavigation from '@/components/Layout/PagesNavigation.vue';
import InfoBanner from '@/components/Layout/InfoBanner.vue';
import TsSectionTabs from '@/components/Talentspace/layout/TsSectionTabs.vue';
import RightSidebar from '@/components/Layout/RightSidebar.vue';
import TermsModal from '@/components/Authorization/TermsModal.vue';
import JobsFilter from '@/components/FilterSidebar/JobsFilter.vue';
import InformationEdunetin from '@/components/Themes/InformationEdunetin.vue';
import { AppModule } from '@/store/modules/app';
import { siteShell } from '@/sites';

@Component({
  components: {
    PagesNavigation,
    TsSectionTabs,
    InfoBanner,
    RightSidebar,
    TermsModal,
    JobsFilter,
    InformationEdunetin,
  },
})
export default class DefaultTemplate extends Vue {
  get sid() {
    return AppModule.sid;
  }

  get shell() {
    return siteShell(this.sid);
  }

  // Embedded as an iframe (?iframe) the portal renders without its own header, navigation and footer
  get showChrome() {
    return !AppModule.isIframe;
  }

  get isTalentspacePage() {
    return Boolean(this.$route.meta?.talentspace);
  }

  // The design styles pages by a class on the same element as .ts-app (e.g. .ts-app.talentspace-welcome-page)
  get pageClass() {
    return this.$route.meta?.pageClass || '';
  }

  // The legacy portal styles (body.portal …) apply only to pages that are not redesigned yet
  @Watch('isTalentspacePage', { immediate: true })
  onPageDesignChange(isTalentspace: boolean) {
    document.body.classList.toggle('portal', !isTalentspace);
  }

  beforeDestroy() {
    document.body.classList.add('portal');
  }

  get rightSidebarClass() {
    // Lines the sidebar up with the page's own tab bar (Workers still has one inside the page)
    return this.$route.name === 'Workers' ? 'right-sidebar-extra-styles' : '';
  }

  get isWelcome() {
    return this.$route.name === 'Welcome';
  }
}
</script>

<style lang="scss" scoped>
.right-sidebar-extra-styles {
  margin-top: 52px;
}
</style>
